import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from './UI';
import { api } from '../services/api';
import { RegistrySplitData } from '../types';
import { useData } from '../App';
import { formatDay, formatMoney } from '../utils/format';

// «Разложить реестр»: выбрать выплаты из кошелька партнёра (строки «Мани»), сумма которых равна сумме реестра
export const RegistrySplitDialog: React.FC<{ txId: string | null; onClose: () => void }> = ({ txId, onClose }) => {
  const { refreshData, showNotification } = useData();
  const [data, setData] = useState<RegistrySplitData | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setData(null); setPicked(new Set());
    if (txId) api.registrySplit(txId).then(setData).catch(e => { showNotification(e?.message || 'Ошибка', 'error'); onClose(); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txId]);
  const sum = useMemo(() => (data?.candidates ?? []).filter(c => picked.has(c.id)).reduce((s, c) => s + c.amount, 0), [data, picked]);
  if (!txId) return null;
  const exact = data ? Math.abs(sum - data.amount) < 0.005 : false;
  const toggle = (id: string) => setPicked(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.saveRegistrySplit(txId, Array.from(picked));
      showNotification(`Реестр разложен: ${r.count} выплат проведены со счёта`, 'success');
      refreshData(); onClose();
    } catch (e: any) { showNotification(e?.message || 'Не удалось сохранить', 'error'); }
    finally { setBusy(false); }
  };
  return (
    <Modal isOpen onClose={onClose} title={data ? `Разложить реестр${data.number ? ` №${data.number}` : ''} от ${formatDay(data.date)}` : 'Реестр'} wide>
      {!data ? <p className="text-sm text-slate-400">Загрузка…</p> : (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">Отметьте выплаты из кошелька партнёра (строки таблицы «Мани», ±14 дней), которые на деле ушли этим реестром.
            Сохранить можно только при точном совпадении суммы — выплаты станут выплатами со счёта по реестру.</p>
          <div className={`sticky top-0 rounded px-3 py-2 font-semibold ${exact ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-50 text-slate-700'}`}>
            Выбрано {formatMoney(sum)} из {formatMoney(data.amount)}
            {!exact && sum > 0 && <span className="ml-2 font-normal text-xs">{sum > data.amount ? 'больше' : 'осталось'} {formatMoney(Math.abs(data.amount - sum))}</span>}
          </div>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded">
            {data.candidates.length === 0 && <p className="p-3 text-xs text-slate-400">Выплат из кошелька за ±14 дней нет</p>}
            {data.candidates.map(c => (
              <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="w-20 text-xs text-slate-500">{formatDay(c.date)}</span>
                <span className="flex-1 min-w-0 truncate" title={c.description}>{c.counterparty}
                  {!c.selfEmployed && <span className="ml-1 text-[10px] text-amber-700">не самозанятый</span>}
                  {c.sourceRow && <span className="ml-1 text-[10px] text-slate-400">стр. {c.sourceRow}</span>}</span>
                <span className="whitespace-nowrap font-medium">{formatMoney(c.amount)}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
            <Button type="button" size="sm" disabled={!exact || busy} onClick={save}>Разложить</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
